/**
 * RetentionPillarsCard — single Settings card consolidating the 5-year
 * retention features: Snooze (off-switch), Parent Digest, and Tutor Memory
 * transparency. One card, three rows, no extra cognitive load.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { BellOff, Brain, Heart, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

export default function RetentionPillarsCard() {
  const [snooze, setSnooze] = useState({ active: false, until: null, finals_mode: false });
  const [parentEmail, setParentEmail] = useState('');
  const [parentEnabled, setParentEnabled] = useState(false);
  const [memory, setMemory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [snz, mem, me] = await Promise.all([
        api.get(apiUrl('/snooze')),
        api.get(apiUrl('/tutor-memory')),
        api.get(apiUrl('/auth/me')),
      ]);
      setSnooze(snz.data || { active: false });
      setMemory(mem.data?.memory || []);
      setParentEmail(me.data?.parent_email || '');
      setParentEnabled(!!me.data?.parent_digest_enabled);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startSnooze = async (hours) => {
    try {
      const { data } = await api.post(apiUrl('/snooze/start'), { hours });
      setSnooze({ active: true, until: data.until, finals_mode: snooze.finals_mode });
      toast.success(`Quiet mode on for ${hours}h — see you later`);
    } catch { toast.error('Could not enable quiet mode'); }
  };
  const endSnooze = async () => {
    try {
      await api.post(apiUrl('/snooze/end'));
      setSnooze({ ...snooze, active: false, until: null });
      toast.success('Welcome back');
    } catch { toast.error('Could not disable quiet mode'); }
  };
  const toggleFinals = async () => {
    try {
      const { data } = await api.post(apiUrl('/snooze/finals-mode'), { enabled: !snooze.finals_mode });
      setSnooze({ ...snooze, finals_mode: data.enabled });
    } catch { toast.error('Could not toggle finals mode'); }
  };

  const saveParent = async () => {
    try {
      await api.put(apiUrl('/parent-digest'), {
        parent_email: parentEmail || null,
        parent_digest_enabled: parentEnabled,
      });
      toast.success('Parent settings saved');
    } catch { toast.error('Could not save'); }
  };
  const testParent = async () => {
    try {
      const { data } = await api.post(apiUrl('/parent-digest/test'));
      if (data.sent) toast.success(`Test digest sent to ${data.to}`);
      else toast.error('Add a parent email first');
    } catch { toast.error('Could not send test'); }
  };

  const wipeMemory = async () => {
    if (!confirm('Clear everything iStudyPro remembers about you? This cannot be undone.')) return;
    try {
      const { data } = await api.delete(apiUrl('/tutor-memory'));
      setMemory([]);
      toast.success(`Forgotten ${data.deleted} note(s)`);
    } catch { toast.error('Could not clear memory'); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      data-testid="retention-pillars-card"
    >
      <Card className="f500-card p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-[var(--isp-glass-border)]">
          <Heart className="w-5 h-5 text-[var(--isp-heading)]" strokeWidth={1.75} />
          <div>
            <h2 className="f500-h2 text-xl">Wellbeing & Privacy</h2>
            <p className="text-sm text-[var(--isp-body)] mt-1">Quiet mode, parent digest, and what your AI remembers about you.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--isp-body)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Snooze / off-switch */}
            <section className="border border-[var(--isp-glass-border)] p-4" data-testid="snooze-section">
              <div className="flex items-start gap-3">
                <BellOff className="w-4 h-4 text-[var(--isp-heading)] mt-0.5 shrink-0" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[var(--isp-heading)]">I'm done for the day</p>
                  <p className="text-xs text-[var(--isp-body)] mt-1">
                    {snooze.active
                      ? <>Quiet until <strong>{new Date(snooze.until).toLocaleString()}</strong>. No reminders or push.</>
                      : <>Pause notifications for a few hours so you can log off without guilt.</>}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {snooze.active ? (
                      <Button size="sm" onClick={endSnooze} className="text-xs h-8" variant="outline" data-testid="snooze-end-btn">
                        End quiet mode
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => startSnooze(2)} className="text-xs h-8" variant="outline" data-testid="snooze-2h-btn">2 hours</Button>
                        <Button size="sm" onClick={() => startSnooze(12)} className="text-xs h-8" variant="outline" data-testid="snooze-12h-btn">12 hours</Button>
                        <Button size="sm" onClick={() => startSnooze(24)} className="text-xs h-8" variant="outline" data-testid="snooze-24h-btn">Tomorrow</Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-[var(--isp-glass-border)]">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--isp-heading)]">Finals-week focus mode</p>
                  <p className="text-xs text-[var(--isp-body)] mt-1">Hide leaderboards & social. Surface only your weakest topics.</p>
                </div>
                <Switch checked={!!snooze.finals_mode} onCheckedChange={toggleFinals} data-testid="finals-mode-toggle" />
              </div>
            </section>

            {/* Parent digest */}
            <section className="border border-[var(--isp-glass-right-border)] p-4" data-testid="parent-section" id="parent">
              <p className="text-sm font-semibold text-[var(--isp-heading)] mb-1">Parent / guardian weekly digest</p>
              <p className="text-xs text-[var(--isp-body)] mb-3">Resend a one-screen study report every Monday. Great for high-school families and scholarship sponsors.</p>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                <Input
                  type="email" placeholder="parent@email.com" value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  className="text-sm h-9" data-testid="parent-email-input"
                />
                <div className="flex items-center gap-2">
                  <Switch checked={parentEnabled} onCheckedChange={setParentEnabled} data-testid="parent-digest-toggle" />
                  <span className="text-xs text-[var(--isp-body)]">Enabled</span>
                </div>
                <Button size="sm" onClick={saveParent} className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-9" data-testid="parent-save-btn">Save</Button>
                <Button size="sm" onClick={testParent} variant="outline" className="text-xs h-9" data-testid="parent-test-btn">Send test</Button>
              </div>
            </section>

            {/* Tutor Memory transparency */}
            <section className="border border-[var(--isp-glass-border)] p-4" data-testid="memory-section">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                  <Brain className="w-4 h-4 text-[var(--isp-heading)] mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div>
                    <p className="text-sm font-semibold text-[var(--isp-heading)]">Things your AI Tutor remembers</p>
                    <p className="text-xs text-[var(--isp-body)] mt-1">
                      {memory.length === 0
                        ? "Empty for now — your tutor will build memory as you chat."
                        : `${memory.length} note${memory.length === 1 ? '' : 's'} recorded across your conversations.`}
                    </p>
                  </div>
                </div>
                {memory.length > 0 && (
                  <button onClick={wipeMemory} className="p-1.5 text-[var(--isp-muted)] hover:text-red-500 transition-colors" title="Clear all" data-testid="memory-wipe-btn" aria-label="Clear all tutor memory">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {memory.length > 0 && (
                <ul className="space-y-1.5 text-xs text-[var(--isp-body)] max-h-44 overflow-y-auto pr-2">
                  {memory.slice(0, 12).map((m, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--isp-muted)] mt-0.5 shrink-0">{m.kind}</span>
                      <span className="flex-1">{m.content}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </Card>
    </motion.div>
  );
}
